import { useState, useEffect, useRef } from "react";
import styles from "./Precificacao.module.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
const parseNum = (v) => parseFloat(String(v || "0").replace(",", ".")) || 0;
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const STORAGE_KEY = "precificacao-v5-historico";
const CALC_KEY    = "precificacao-v5-rascunho";
const THEME_KEY   = "precificacao-v5-tema";

const EMPTY_INGREDIENTE = () => ({
  id: uid(), nome: "", valorCompra: "", quantidadeProduzida: "", unidade: "unid",
});

const EMPTY_EXTRA = () => ({
  id: uid(), nome: "", tipo: "unidade",
  valor: "",
  valorMensal: "", producaoMensal: "",
});

const EMPTY_CALC = {
  ingredientes: [EMPTY_INGREDIENTE()],

  tempoHoras: "",
  tempoMinutos: "",
  valorHora: "",
  tempoUnidadesLote: "1",

  gas: {
    ativo: false,
    valorMensal: "",
    minutosUso: "",
    totalMinutosMes: "3000",
    unidadesLote: "1",
  },

  energia: {
    ativo: false,
    kwhMes: "",
    valorKwh: "0.85",
    horasUso: "",
    totalHorasMes: "720",
    unidadesLote: "1",
  },

  extras: [],
  margemLucro: "40",
  margemManual: "",
  impostos: "0",

  // ── NOVO ──────────────────────────────────────────────────────────────
  precoPraticado: "",   // preço que o usuário já pratica (ou quer testar)
};

// ─── Cálculo central ──────────────────────────────────────────────────────────

function calcular(c) {
  // ── Ingredientes ─────────────────────────────────────────────────────────────
  const detIngredientes = c.ingredientes.map(ing => {
    const v = parseNum(ing.valorCompra);
    const q = parseNum(ing.quantidadeProduzida);
    return { ...ing, custoPorUnidade: (v > 0 && q > 0) ? round2(v / q) : 0 };
  });
  const custoIngredientes = round2(detIngredientes.reduce((s, i) => s + i.custoPorUnidade, 0));

  // ── Total bruto de matéria-prima (soma dos valorCompra) ──────────────────────
  const totalBrutoMateriais = round2(
    c.ingredientes.reduce((s, ing) => s + parseNum(ing.valorCompra), 0)
  );

  // ── Mão de obra ──────────────────────────────────────────────────────────────
  const horasTotais       = parseNum(c.tempoHoras) + parseNum(c.tempoMinutos) / 60;
  const custoLoteTempo    = round2(horasTotais * parseNum(c.valorHora));
  const unidadesTempoLote = Math.max(1, parseNum(c.tempoUnidadesLote));
  const custoMaoObra      = round2(custoLoteTempo / unidadesTempoLote);

  // ── Gás ──────────────────────────────────────────────────────────────────────
  let custoGas = 0, custoGasLote = 0;
  if (c.gas.ativo) {
    const minUso  = parseNum(c.gas.minutosUso);
    const minMes  = parseNum(c.gas.totalMinutosMes) || 3000;
    const valMes  = parseNum(c.gas.valorMensal);
    const unidGas = Math.max(1, parseNum(c.gas.unidadesLote));
    if (minMes > 0) {
      custoGasLote = round2((minUso / minMes) * valMes);
      custoGas     = round2(custoGasLote / unidGas);
    }
  }

  // ── Energia ──────────────────────────────────────────────────────────────────
  let custoEnergia = 0, custoEnergiaLote = 0;
  if (c.energia.ativo) {
    const hUso    = parseNum(c.energia.horasUso);
    const hMes    = parseNum(c.energia.totalHorasMes) || 720;
    const kwh     = parseNum(c.energia.kwhMes);
    const vKwh    = parseNum(c.energia.valorKwh);
    const unidEne = Math.max(1, parseNum(c.energia.unidadesLote));
    if (hMes > 0) {
      custoEnergiaLote = round2((hUso / hMes) * kwh * vKwh);
      custoEnergia     = round2(custoEnergiaLote / unidEne);
    }
  }

  // ── Extras ───────────────────────────────────────────────────────────────────
  const detExtras = c.extras.map(e => {
    const custo = e.tipo === "mensal"
      ? round2(parseNum(e.valorMensal) / (parseNum(e.producaoMensal) || 1))
      : parseNum(e.valor);
    return { ...e, custoUnidade: custo };
  });
  const custoExtras = round2(detExtras.reduce((s, e) => s + e.custoUnidade, 0));

  // ── Total ─────────────────────────────────────────────────────────────────────
  const custoTotal = round2(
    custoIngredientes + custoMaoObra + custoGas + custoEnergia + custoExtras
  );

  // ── Margem ───────────────────────────────────────────────────────────────────
  const mg  = Math.min(parseNum(c.margemManual || c.margemLucro), 99) / 100;
  const imp = parseNum(c.impostos) / 100;

  const calcPreco = (margem) => {
    const d = 1 - margem - imp;
    return d > 0 ? round2(custoTotal / d) : 0;
  };

  const precoEmpate      = imp < 1 ? round2(custoTotal / (1 - imp)) : 0;
  const lucroEmpate      = round2(precoEmpate - custoTotal - precoEmpate * imp);

  const mgConservador    = Math.max(mg / 2, 0.10);
  const margemConservador = Math.round(mgConservador * 100);
  const precoConservador = calcPreco(mgConservador);
  const lucroConservador = round2(precoConservador * mgConservador);

  const precoIdeal       = calcPreco(mg);
  const lucroIdeal       = round2(precoIdeal * mg);

  const mgPremium        = Math.min(mg + 0.2, 0.99);
  const margemPremium    = Math.round(mgPremium * 100);
  const precoPremium     = calcPreco(mgPremium);
  const lucroPremium     = round2(precoPremium * mgPremium);

  // ── NOVO: análise do preço praticado ────────────────────────────────────────
  const precoPraticado = parseNum(c.precoPraticado);
  let analisePraticado = null;
  if (precoPraticado > 0 && custoTotal > 0) {
    const impostoPraticado = round2(precoPraticado * imp);
    const lucroPraticado   = round2(precoPraticado - custoTotal - impostoPraticado);
    const margemPraticada  = precoPraticado > 0
      ? round2((lucroPraticado / precoPraticado) * 100)
      : 0;
    const roi              = custoTotal > 0
      ? round2((lucroPraticado / custoTotal) * 100)
      : 0;

    let status = "neutro";
    let mensagem = "";
    if (precoPraticado < custoTotal) {
      status = "prejuizo";
      mensagem = `Você está vendendo abaixo do custo. Prejuízo de ${fmtBRL(Math.abs(lucroPraticado))} por unidade.`;
    } else if (precoPraticado < precoEmpate) {
      status = "abaixo";
      mensagem = `Preço não cobre impostos. Falta ${fmtBRL(round2(precoEmpate - precoPraticado))} para atingir o ponto de equilíbrio.`;
    } else if (precoPraticado < precoConservador) {
      status = "baixo";
      mensagem = `Cobre os custos, mas com margem muito baixa (${margemPraticada}%). Considere aumentar o preço.`;
    } else if (precoPraticado >= precoIdeal) {
      status = "otimo";
      mensagem = `Ótimo! Você está na faixa ideal ou acima, com margem de ${margemPraticada}%.`;
    } else {
      status = "ok";
      mensagem = `Preço na faixa conservadora. Margem de ${margemPraticada}%. Há espaço para aumentar.`;
    }

    analisePraticado = {
      lucro: lucroPraticado,
      margem: margemPraticada,
      roi,
      status,
      mensagem,
      diferencaIdeal: round2(precoIdeal - precoPraticado),
    };
  }

  // ── NOVO: insights automáticos ──────────────────────────────────────────────
  const insights = [];

  // % do custo que são ingredientes
  if (custoTotal > 0) {
    const pctIng = Math.round((custoIngredientes / custoTotal) * 100);
    if (pctIng > 70) {
      insights.push({
        tipo: "alerta",
        texto: `Ingredientes representam ${pctIng}% do custo total. Avalie fornecedores alternativos ou ajuste a receita para melhorar sua margem.`,
      });
    }
  }

  // Mão de obra zerada
  if (custoMaoObra === 0) {
    insights.push({
      tipo: "dica",
      texto: "Você não incluiu custo de mão de obra. Valorize seu tempo — mesmo que seja você mesmo produzindo!",
    });
  }

  // Margem abaixo de 20%
  if (mg > 0 && mg < 0.20) {
    insights.push({
      tipo: "alerta",
      texto: `Margem de ${Math.round(mg * 100)}% é baixa. Considere revisar custos ou reposicionar o produto para garantir sustentabilidade do negócio.`,
    });
  }

  // ROI/markup implícito
  if (custoTotal > 0 && precoIdeal > 0) {
    const markup = round2(((precoIdeal - custoTotal) / custoTotal) * 100);
    insights.push({
      tipo: "info",
      texto: `Markup do preço ideal: ${markup}% sobre o custo (cada R$ 1,00 investido retorna R$ ${round2(precoIdeal / custoTotal).toFixed(2)}).`,
    });
  }

  // Impostos não configurados
  if (imp === 0) {
    insights.push({
      tipo: "dica",
      texto: "Impostos zerados. Se você emite nota, vende no iFood ou tem MEI com obrigações fiscais, inclua o percentual para não ser surpreendido.",
    });
  }

  const precoMinimo = precoEmpate;
  const lucro       = lucroIdeal;

  return {
    detIngredientes, custoIngredientes,
    totalBrutoMateriais,
    custoLoteTempo, custoMaoObra, unidadesTempoLote,
    custoGasLote, custoGas,
    custoEnergiaLote, custoEnergia,
    detExtras, custoExtras,
    custoTotal,
    precoEmpate, lucroEmpate,
    precoConservador, lucroConservador, margemConservador,
    precoIdeal, lucroIdeal,
    precoPremium, lucroPremium, margemPremium,
    analisePraticado,
    insights,
    // legado
    precoMinimo, lucro,
    margemUsada: Math.round(mg * 100),
  };
}

// ─── Ícones ───────────────────────────────────────────────────────────────────

const IcoPlus    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IcoTrash   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>;
const IcoPrint   = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>;
const IcoSave    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const IcoHistory = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 3"/><path d="M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5"/></svg>;
const IcoMoon    = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
const IcoSun     = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>;
const IcoNew     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>;
const IcoChevron = ({ open }) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}><polyline points="6 9 12 15 18 9"/></svg>;
const IcoInfo    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;

// ─── Componentes base ─────────────────────────────────────────────────────────

const Field = ({ label, hint, children, style }) => (
  <div className={styles.field} style={style}>
    {label && <label className={styles.fLabel}>{label}{hint && <span className={styles.fHint}>{hint}</span>}</label>}
    {children}
  </div>
);

const NumInp = ({ prefix, suffix, style, ...rest }) => (
  <div className={styles.fWrap} style={style}>
    {prefix && <span className={styles.fPre}>{prefix}</span>}
    <input type="number" min="0" step="0.01"
      className={`${styles.fInput} ${prefix ? styles.hasPre : ""} ${suffix ? styles.hasSuf : ""}`}
      {...rest} />
    {suffix && <span className={styles.fSuf}>{suffix}</span>}
  </div>
);

const TxtInp = ({ style, ...rest }) => (
  <input type="text" className={styles.fInput} style={style} {...rest} />
);

const Toggle = ({ checked, onChange, label }) => (
  <label className={styles.toggle}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
    <span className={styles.toggleTrack}><span className={styles.toggleThumb} /></span>
    {label && <span className={styles.toggleLabel}>{label}</span>}
  </label>
);

const Section = ({ title, icon, badge, open, onToggle, children, accent }) => (
  <div className={`${styles.section} ${accent ? styles[`section__${accent}`] : ""}`}>
    <button className={styles.sectionHead} onClick={onToggle} type="button">
      <span className={styles.sectionHeadLeft}>
        {icon && <span className={styles.sectionIcon}>{icon}</span>}
        <span className={styles.sectionTitle}>{title}</span>
        {badge != null && <span className={styles.sectionBadge}>{badge}</span>}
      </span>
      <IcoChevron open={open} />
    </button>
    {open && <div className={styles.sectionBody}>{children}</div>}
  </div>
);

const CalcBox = ({ label, lote, unidade, show }) => {
  if (!show) return null;
  return (
    <div className={styles.calcBox}>
      <div className={styles.calcBoxItem}>
        <span>Custo da sessão</span>
        <strong>{fmtBRL(lote)}</strong>
      </div>
      <div className={styles.calcBoxDivider} />
      <div className={`${styles.calcBoxItem} ${styles.calcBoxDest}`}>
        <span>Por unidade</span>
        <strong>{fmtBRL(unidade)}</strong>
      </div>
    </div>
  );
};

// ─── Card de ingrediente ──────────────────────────────────────────────────────

const IngredienteRow = ({ ing, onChange, onRemove, showRemove }) => {
  const v = parseNum(ing.valorCompra);
  const q = parseNum(ing.quantidadeProduzida);
  const custoPorUnidade = (v > 0 && q > 0) ? round2(v / q) : 0;

  return (
    <div className={styles.ingCard}>
      <div className={styles.ingCardHead}>
        <TxtInp
          placeholder="Nome do ingrediente (ex: Farinha de trigo, Manteiga…)"
          value={ing.nome}
          onChange={e => onChange(ing.id, "nome", e.target.value)}
          style={{ flex: 1 }}
        />
        {showRemove && (
          <button className={styles.btnDel} onClick={() => onRemove(ing.id)} type="button">
            <IcoTrash />
          </button>
        )}
      </div>
      <div className={styles.ingCardFields}>
        <Field label="Valor de compra">
          <NumInp prefix="R$" placeholder="0,00"
            value={ing.valorCompra}
            onChange={e => onChange(ing.id, "valorCompra", e.target.value)} />
        </Field>
        <Field label="Qtd. produzida" hint="com esse valor">
          <div className={styles.fWrapGroup}>
            <NumInp placeholder="Ex: 20"
              value={ing.quantidadeProduzida}
              onChange={e => onChange(ing.id, "quantidadeProduzida", e.target.value)}
              style={{ flex: 1 }} />
            <select className={styles.select}
              value={ing.unidade}
              onChange={e => onChange(ing.id, "unidade", e.target.value)}>
              <option value="unid">unid</option>
              <option value="kg">kg</option>
              <option value="g">g</option>
              <option value="L">L</option>
              <option value="ml">ml</option>
              <option value="m">m</option>
              <option value="pç">pç</option>
            </select>
          </div>
        </Field>
        <div className={`${styles.ingCusto} ${custoPorUnidade > 0 ? styles.ingCustoOn : ""}`}>
          <span>Custo/unidade</span>
          <strong>{fmtBRL(custoPorUnidade)}</strong>
        </div>
      </div>
    </div>
  );
};

// ─── Card de extra / sistema ──────────────────────────────────────────────────

const ExtraCard = ({ extra, onChange, onRemove }) => {
  const isMensal = extra.tipo === "mensal";
  const custoUnidade = isMensal
    ? round2(parseNum(extra.valorMensal) / (parseNum(extra.producaoMensal) || 1))
    : parseNum(extra.valor);

  return (
    <div className={styles.extraCard}>
      <div className={styles.extraCardHead}>
        <TxtInp
          placeholder="Nome (ex: Caixa, Canva Pro, iFood, Rótulo…)"
          value={extra.nome}
          onChange={e => onChange(extra.id, "nome", e.target.value)}
          style={{ flex: 1 }}
        />
        <div className={styles.tipoToggle}>
          <button type="button"
            className={`${styles.tipoBtn} ${!isMensal ? styles.tipoBtnOn : ""}`}
            onClick={() => onChange(extra.id, "tipo", "unidade")}>
            Por unidade
          </button>
          <button type="button"
            className={`${styles.tipoBtn} ${isMensal ? styles.tipoBtnOn : ""}`}
            onClick={() => onChange(extra.id, "tipo", "mensal")}>
            Mensal ÷ prod.
          </button>
        </div>
        <button className={styles.btnDel} onClick={() => onRemove(extra.id)} type="button">
          <IcoTrash />
        </button>
      </div>
      <div className={styles.extraCardFields}>
        {isMensal ? (
          <>
            <Field label="Valor mensal">
              <NumInp prefix="R$" placeholder="0,00"
                value={extra.valorMensal}
                onChange={e => onChange(extra.id, "valorMensal", e.target.value)} />
            </Field>
            <Field label="Produções por mês" hint="para dividir">
              <NumInp placeholder="Ex: 30"
                value={extra.producaoMensal}
                onChange={e => onChange(extra.id, "producaoMensal", e.target.value)} />
            </Field>
          </>
        ) : (
          <Field label="Valor por unidade produzida">
            <NumInp prefix="R$" placeholder="0,00"
              value={extra.valor}
              onChange={e => onChange(extra.id, "valor", e.target.value)} />
          </Field>
        )}
        {custoUnidade > 0 && (
          <div className={styles.extraCustoInline}>
            <span>{isMensal ? "Custo/unidade" : "Valor/un"}</span>
            <strong>{fmtBRL(custoUnidade)}</strong>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Bloco: Análise do preço praticado ───────────────────────────────────────

const statusConfig = {
  prejuizo: { bg: "#fff1f0", border: "#ffccc7", color: "#cf1322", emoji: "🚨", label: "Prejuízo" },
  abaixo:   { bg: "#fff7e6", border: "#ffd591", color: "#d46b08", emoji: "⚠️", label: "Abaixo do equilíbrio" },
  baixo:    { bg: "#fffbe6", border: "#ffe58f", color: "#ad8b00", emoji: "📉", label: "Margem baixa" },
  ok:       { bg: "#f6ffed", border: "#b7eb8f", color: "#389e0d", emoji: "✅", label: "Aceitável" },
  otimo:    { bg: "#e6f4ff", border: "#91caff", color: "#0958d9", emoji: "🎯", label: "Ótimo preço" },
  neutro:   { bg: "#f5f5f5", border: "#d9d9d9", color: "#595959", emoji: "➖", label: "Neutro" },
};

function AnalisePraticado({ analise, precoIdeal, precoPraticado }) {
  if (!analise) return null;
  const cfg = statusConfig[analise.status] || statusConfig.neutro;

  return (
    <div style={{
      background: cfg.bg,
      border: `1.5px solid ${cfg.border}`,
      borderRadius: 10,
      padding: "14px 16px",
      marginTop: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 18 }}>{cfg.emoji}</span>
        <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>{cfg.label}</span>
      </div>

      <p style={{ fontSize: 13, color: "#333", marginBottom: 12, lineHeight: 1.5 }}>
        {analise.mensagem}
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {[
          { label: "Lucro/unidade", value: fmtBRL(analise.lucro), color: analise.lucro >= 0 ? "#389e0d" : "#cf1322" },
          { label: "Margem real", value: `${analise.margem}%`, color: analise.margem >= 20 ? "#0958d9" : "#cf1322" },
          { label: "ROI", value: `${analise.roi}%`, color: analise.roi >= 0 ? "#389e0d" : "#cf1322" },
        ].map(item => (
          <div key={item.label} style={{
            background: "rgba(255,255,255,0.7)",
            borderRadius: 7,
            padding: "8px 10px",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>{item.label}</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: item.color }}>{item.value}</div>
          </div>
        ))}
      </div>

      {analise.diferencaIdeal > 0 && (
        <div style={{
          marginTop: 10,
          fontSize: 12,
          color: "#595959",
          background: "rgba(255,255,255,0.6)",
          borderRadius: 6,
          padding: "7px 10px",
        }}>
          💡 Aumentar o preço em <strong style={{ color: cfg.color }}>{fmtBRL(analise.diferencaIdeal)}</strong> levaria
          ao preço ideal ({fmtBRL(precoIdeal)}).
        </div>
      )}
    </div>
  );
}

// ─── Bloco: Insights automáticos ─────────────────────────────────────────────

const insightStyle = {
  alerta: { bg: "#fff7e6", border: "#ffd591", color: "#d46b08", icon: "⚠️" },
  dica:   { bg: "#f0f5ff", border: "#adc6ff", color: "#1d39c4", icon: "💡" },
  info:   { bg: "#f9f0ff", border: "#d3adf7", color: "#531dab", icon: "📊" },
};

function Insights({ insights }) {
  if (!insights || insights.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
      <p style={{ fontSize: 12, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", margin: 0 }}>
        Insights
      </p>
      {insights.map((ins, i) => {
        const s = insightStyle[ins.tipo] || insightStyle.info;
        return (
          <div key={i} style={{
            background: s.bg,
            border: `1px solid ${s.border}`,
            borderRadius: 8,
            padding: "10px 13px",
            display: "flex",
            gap: 9,
            alignItems: "flex-start",
          }}>
            <span style={{ fontSize: 15, flexShrink: 0 }}>{s.icon}</span>
            <span style={{ fontSize: 12.5, color: "#333", lineHeight: 1.55 }}>{ins.texto}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Modal de impressão ───────────────────────────────────────────────────────

function ImprimirModal({ calc, res, onClose }) {
  const printRef = useRef();

  const handlePrint = () => {
    const html = printRef.current.innerHTML;
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Precificação</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Segoe UI',Arial,sans-serif;color:#111;padding:28px;font-size:13px}
        h1{font-size:20px;font-weight:800;margin-bottom:3px}
        .sub{color:#888;font-size:11px;margin-bottom:22px}
        table{width:100%;border-collapse:collapse;margin-bottom:16px}
        th{text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#777;padding:5px 8px;background:#f7f7fa}
        td{padding:6px 8px;border-bottom:1px solid #eee;font-size:12.5px}
        td.r{text-align:right;font-weight:700}
        td.muted{color:#888;font-size:11px}
        .total{display:flex;justify-content:space-between;padding:9px 13px;background:#eef3ff;border-radius:6px;font-weight:700;font-size:14px;margin-bottom:16px}
        .precos{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:14px}
        .pc{border-radius:8px;padding:12px;text-align:center}
        .pc .tag{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;margin-bottom:5px}
        .pc .val{font-size:19px;font-weight:800}
        .pc .s{font-size:10px;color:#666;margin-top:3px}
        .pc.emp{background:#fff8e1;border:1px solid #ffcc80}.pc.emp .tag,.pc.emp .val{color:#e65100}
        .pc.con{background:#fff9e6;border:1px solid #ffe082}.pc.con .tag,.pc.con .val{color:#b45309}
        .pc.ide{background:#e3f2fd;border:2px solid #1565c0}.pc.ide .tag,.pc.ide .val{color:#1565c0}
        .pc.pre{background:#f3e5f5;border:1px solid #ce93d8}.pc.pre .tag,.pc.pre .val{color:#7b1fa2}
        .praticado{background:#f0fdf4;border:1.5px solid #86efac;border-radius:8px;padding:12px 14px;margin-bottom:14px}
        .praticado h3{font-size:12px;font-weight:700;color:#15803d;margin-bottom:8px}
        .praticado-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
        .praticado-item{text-align:center;background:#fff;border-radius:6px;padding:7px}
        .praticado-item .plabel{font-size:10px;color:#888;margin-bottom:2px}
        .praticado-item .pval{font-size:14px;font-weight:800}
        .insights{margin-bottom:14px}
        .insights h3{font-size:11px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px}
        .insight{background:#f7f7fa;border-radius:6px;padding:7px 10px;font-size:11.5px;color:#444;margin-bottom:5px;line-height:1.5}
        .lucro{background:#e8f5e9;border:1px solid #a5d6a7;border-radius:7px;padding:10px 14px;display:flex;justify-content:space-between;align-items:center}
        .lucro strong{color:#2e7d32;font-size:14px}
        .rodape{margin-top:24px;padding-top:10px;border-top:1px solid #eee;font-size:10px;color:#bbb;text-align:center}
        @media print{body{padding:16px}}
      </style></head><body>${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const data = new Date().toLocaleString("pt-BR");
  const margemUsada = res.margemUsada;
  const precoPraticado = parseNum(calc.precoPraticado);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHead}>
          <span className={styles.modalTitle}>Pré-visualização de impressão</span>
          <button className={styles.modalClose} onClick={onClose}>✕</button>
        </div>

        <div className={styles.modalBody} ref={printRef}>
          <h1>Análise de Precificação</h1>
          <p className="sub">Gerada em {data}</p>

          <table>
            <thead>
              <tr><th>Ingrediente / Matéria-prima</th><th>Valor compra</th><th>Qtd. produz</th><th className="r">Custo/unid.</th></tr>
            </thead>
            <tbody>
              {res.detIngredientes.map(i => (
                <tr key={i.id}>
                  <td>{i.nome || "—"}</td>
                  <td>{fmtBRL(i.valorCompra)}</td>
                  <td>{i.quantidadeProduzida} {i.unidade}</td>
                  <td className="r">{fmtBRL(i.custoPorUnidade)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan="2"><em style={{fontSize:11,color:"#888"}}>Total investido em matéria-prima (lote bruto)</em></td>
                <td colSpan="2" className="r">{fmtBRL(res.totalBrutoMateriais)}</td>
              </tr>
              <tr><td colSpan="3"><strong>Total ingredientes por unidade</strong></td><td className="r">{fmtBRL(res.custoIngredientes)}</td></tr>
            </tbody>
          </table>

          <table>
            <thead><tr><th>Custo adicional</th><th>Detalhe do lote</th><th className="r">Custo/unidade</th></tr></thead>
            <tbody>
              {res.custoMaoObra > 0 && (
                <tr>
                  <td>⏱️ Mão de obra</td>
                  <td className="muted">{calc.tempoHoras || 0}h {calc.tempoMinutos || 0}min × {fmtBRL(calc.valorHora)}/h ÷ {res.unidadesTempoLote} un = {fmtBRL(res.custoLoteTempo)}</td>
                  <td className="r">{fmtBRL(res.custoMaoObra)}</td>
                </tr>
              )}
              {calc.gas.ativo && res.custoGas > 0 && (
                <tr>
                  <td>🔥 Gás</td>
                  <td className="muted">{calc.gas.minutosUso} min uso · sessão {fmtBRL(res.custoGasLote)} ÷ {calc.gas.unidadesLote} un</td>
                  <td className="r">{fmtBRL(res.custoGas)}</td>
                </tr>
              )}
              {calc.energia.ativo && res.custoEnergia > 0 && (
                <tr>
                  <td>⚡ Energia</td>
                  <td className="muted">{calc.energia.horasUso}h uso · sessão {fmtBRL(res.custoEnergiaLote)} ÷ {calc.energia.unidadesLote} un</td>
                  <td className="r">{fmtBRL(res.custoEnergia)}</td>
                </tr>
              )}
              {res.detExtras.map(e => e.custoUnidade > 0 && (
                <tr key={e.id}>
                  <td>📎 {e.nome || "Extra"}{e.tipo === "mensal" ? " (mensal)" : ""}</td>
                  <td className="muted">{e.tipo === "mensal" ? `${fmtBRL(e.valorMensal)}/mês ÷ ${e.producaoMensal} prod.` : "Valor fixo"}</td>
                  <td className="r">{fmtBRL(e.custoUnidade)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="total">
            <span>Custo total por unidade</span>
            <span>{fmtBRL(res.custoTotal)}</span>
          </div>

          {/* Preço praticado na impressão */}
          {res.analisePraticado && (
            <div className="praticado">
              <h3>📌 Análise do preço praticado: {fmtBRL(precoPraticado)}</h3>
              <div className="praticado-grid">
                <div className="praticado-item">
                  <div className="plabel">Lucro/unidade</div>
                  <div className="pval" style={{ color: res.analisePraticado.lucro >= 0 ? "#15803d" : "#dc2626" }}>
                    {fmtBRL(res.analisePraticado.lucro)}
                  </div>
                </div>
                <div className="praticado-item">
                  <div className="plabel">Margem real</div>
                  <div className="pval">{res.analisePraticado.margem}%</div>
                </div>
                <div className="praticado-item">
                  <div className="plabel">ROI</div>
                  <div className="pval">{res.analisePraticado.roi}%</div>
                </div>
              </div>
              <p style={{ marginTop: 8, fontSize: 11.5, color: "#374151" }}>{res.analisePraticado.mensagem}</p>
            </div>
          )}

          <div className="precos">
            <div className="pc emp"><div className="tag">⚠ Empate</div><div className="val">{fmtBRL(res.precoEmpate)}</div><div className="s">Lucro: {fmtBRL(res.lucroEmpate)} — só cobre custos</div></div>
            <div className="pc con"><div className="tag">Conservador</div><div className="val">{fmtBRL(res.precoConservador)}</div><div className="s">Margem {res.margemConservador}% · Lucro {fmtBRL(res.lucroConservador)}/un</div></div>
            <div className="pc ide"><div className="tag">★ Ideal</div><div className="val">{fmtBRL(res.precoIdeal)}</div><div className="s">Margem {margemUsada}% · Lucro {fmtBRL(res.lucroIdeal)}/un</div></div>
            <div className="pc pre"><div className="tag">Premium</div><div className="val">{fmtBRL(res.precoPremium)}</div><div className="s">Margem {res.margemPremium}% · Lucro {fmtBRL(res.lucroPremium)}/un</div></div>
          </div>

          {/* Insights na impressão */}
          {res.insights.length > 0 && (
            <div className="insights">
              <h3>Insights</h3>
              {res.insights.map((ins, i) => (
                <div key={i} className="insight">{ins.texto}</div>
              ))}
            </div>
          )}

          <div className="lucro">
            <span>Lucro estimado no preço ideal</span>
            <strong>{fmtBRL(res.lucro)} / unidade</strong>
          </div>

          <p className="rodape">Gerado por FluxoEasy Precificação</p>
        </div>

        <div className={styles.modalFoot}>
          <button className={styles.btnSecondary} onClick={onClose}>Fechar</button>
          <button className={styles.btnPrimary} onClick={handlePrint}><IcoPrint /> Imprimir / Salvar PDF</button>
        </div>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function Precificacao() {
  const [tema, setTema] = useState(() => {
    try { return localStorage.getItem(THEME_KEY) || "light"; } catch { return "light"; }
  });

  const [calc, setCalc] = useState(() => {
    try {
      const r = localStorage.getItem(CALC_KEY);
      if (r) {
        const p = JSON.parse(r);
        // Migração v3 → v5
        if (!p.ingredientes && p.produto) {
          const ing = EMPTY_INGREDIENTE();
          ing.nome = p.produto.nome || "";
          ing.valorCompra = p.produto.valorCompra || "";
          ing.quantidadeProduzida = p.produto.quantidadeProduzida || "";
          ing.unidade = p.produto.unidade || "unid";
          p.ingredientes = [ing];
          delete p.produto; delete p.sistemaPago;
        }
        if (!p.tempoUnidadesLote) p.tempoUnidadesLote = "1";
        if (p.gas && !p.gas.unidadesLote) p.gas.unidadesLote = "1";
        if (p.energia && !p.energia.unidadesLote) p.energia.unidadesLote = "1";
        if (p.precoPraticado === undefined) p.precoPraticado = "";
        return { ...EMPTY_CALC, ...p };
      }
    } catch {}
    return { ...EMPTY_CALC };
  });

  const [historico, setHistorico] = useState(() => {
    try { const r = localStorage.getItem(STORAGE_KEY); return r ? JSON.parse(r) : []; }
    catch { return []; }
  });

  const [sec, setSec] = useState({
    ingredientes: true, tempo: true, gas: false, energia: false, extras: false, margens: true,
  });
  const [histOpen,  setHistOpen]  = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [savedMsg,  setSavedMsg]  = useState(false);

  useEffect(() => { try { localStorage.setItem(CALC_KEY,    JSON.stringify(calc));      } catch {} }, [calc]);
  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(historico)); } catch {} }, [historico]);
  useEffect(() => {
    try { localStorage.setItem(THEME_KEY, tema); } catch {}
    document.documentElement.setAttribute("data-theme", tema);
  }, [tema]);

  const togSec     = (k) => setSec(p => ({ ...p, [k]: !p[k] }));
  const setC       = (f, v) => setCalc(p => ({ ...p, [f]: v }));
  const setGas     = (f, v) => setCalc(p => ({ ...p, gas:     { ...p.gas,     [f]: v } }));
  const setEnergia = (f, v) => setCalc(p => ({ ...p, energia: { ...p.energia, [f]: v } }));

  const addIng = () => setCalc(p => ({ ...p, ingredientes: [...p.ingredientes, EMPTY_INGREDIENTE()] }));
  const delIng = (id) => setCalc(p => ({ ...p, ingredientes: p.ingredientes.filter(i => i.id !== id) }));
  const setIng = (id, f, v) => setCalc(p => ({
    ...p, ingredientes: p.ingredientes.map(i => i.id === id ? { ...i, [f]: v } : i),
  }));

  const addExtra = () => setCalc(p => ({ ...p, extras: [...p.extras, EMPTY_EXTRA()] }));
  const delExtra = (id) => setCalc(p => ({ ...p, extras: p.extras.filter(e => e.id !== id) }));
  const setExtra = (id, f, v) => setCalc(p => ({
    ...p, extras: p.extras.map(e => e.id === id ? { ...e, [f]: v } : e),
  }));

  const res     = calcular(calc);
  const custoOk = res.custoTotal > 0;

  const margemExibida = calc.margemManual !== "" ? calc.margemManual : calc.margemLucro;
  const margemNum     = Math.min(parseNum(margemExibida), 99);

  const handleMargemSlider = (v) => {
    setC("margemLucro", v);
    setC("margemManual", "");
  };

  const handleMargemManual = (v) => {
    setC("margemManual", v);
    const n = parseNum(v);
    if (n >= 5 && n <= 80) setC("margemLucro", String(Math.round(n)));
  };

  const salvar = () => {
    if (!custoOk) return;
    const agora = new Date().toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const nomeRef = calc.ingredientes.map(i => i.nome).filter(Boolean).join(", ") || "Sem nome";
    setHistorico(p => [{
      id: uid(), data: agora, nome: nomeRef,
      custoTotal: res.custoTotal, precoIdeal: res.precoIdeal,
      calc: JSON.parse(JSON.stringify(calc)),
    }, ...p].slice(0, 50));
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2000);
  };

  const carregar = (item) => {
    setCalc({ ...EMPTY_CALC, ...item.calc });
    setHistOpen(false);
  };

  const novo = () => {
    if (window.confirm("Iniciar novo cálculo? O rascunho atual será perdido.")) {
      setCalc({ ...EMPTY_CALC, ingredientes: [EMPTY_INGREDIENTE()] });
    }
  };

  return (
    <div className={styles.app} data-theme={tema}>

      {/* ── Cabeçalho ── */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.logo}>
            <span className={styles.logoDot} />
            <span className={styles.logoText}>Precificação</span>
          </div>
          <p className={styles.headerSub}>Calcule o preço ideal para seu produto</p>
        </div>
        <div className={styles.headerRight}>
          <button className={`${styles.iconBtn} ${histOpen ? styles.iconBtnOn : ""}`}
            onClick={() => setHistOpen(v => !v)} title="Histórico">
            <IcoHistory />
            {historico.length > 0 && <span className={styles.badge}>{historico.length}</span>}
          </button>
          <button className={styles.btnOutline} onClick={novo}><IcoNew /> Novo</button>
          <button className={styles.btnPrimary} onClick={salvar} disabled={!custoOk}>
            <IcoSave /> {savedMsg ? "Salvo!" : "Salvar"}
          </button>
        </div>
      </header>

      {/* ── Histórico ── */}
      {histOpen && (
        <div className={styles.histPanel}>
          <div className={styles.histPanelHead}>
            <span className={styles.histPanelTitle}>Histórico de cálculos</span>
            {historico.length > 0 && (
              <button className={styles.btnDanger}
                onClick={() => { if (window.confirm("Limpar todo o histórico?")) setHistorico([]); }}>
                Limpar tudo
              </button>
            )}
          </div>
          {historico.length === 0
            ? <p className={styles.histVazio}>Nenhum cálculo salvo ainda.</p>
            : (
              <div className={styles.histLista}>
                {historico.map(h => (
                  <div className={styles.histItem} key={h.id}>
                    <div className={styles.histInfo}>
                      <span className={styles.histNome}>{h.nome}</span>
                      <span className={styles.histMeta}>
                        {h.data} · Custo: {fmtBRL(h.custoTotal)} · Ideal: {fmtBRL(h.precoIdeal)}
                      </span>
                    </div>
                    <div className={styles.histAcoes}>
                      <button className={styles.btnSmall} onClick={() => carregar(h)}>Carregar</button>
                      <button className={styles.btnSmallDanger}
                        onClick={() => setHistorico(p => p.filter(x => x.id !== h.id))}>
                        <IcoTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {/* ── Layout ── */}
      <div className={styles.layout}>
        <div className={styles.colLeft}>

          {/* Ingredientes */}
          <Section title="Ingredientes / Matérias-primas" icon="📦"
            badge={calc.ingredientes.length > 1 ? `${calc.ingredientes.length} itens` : null}
            open={sec.ingredientes} onToggle={() => togSec("ingredientes")} accent="blue">

            <p className={styles.sectionDesc}>
              Adicione todos os ingredientes ou matérias-primas.
              Informe <strong>quanto custou</strong> e <strong>quantas unidades finais</strong> aquela compra rende.
            </p>

            {calc.ingredientes.map(ing => (
              <IngredienteRow key={ing.id} ing={ing}
                onChange={setIng} onRemove={delIng}
                showRemove={calc.ingredientes.length > 1} />
            ))}

            {/* ── NOVO: total bruto de materiais ── */}
            {res.totalBrutoMateriais > 0 && (
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                background: "var(--surface-2, #f7f9ff)",
                border: "1px solid var(--border, #e2e8f0)",
                borderRadius: 8,
                padding: "10px 14px",
                marginTop: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "var(--text-2, #64748b)" }}>
                    💰 Total investido em compras (lote bruto)
                  </span>
                  <strong style={{ fontSize: 14, color: "var(--text-1, #1e293b)" }}>
                    {fmtBRL(res.totalBrutoMateriais)}
                  </strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "var(--text-2, #64748b)" }}>
                    📦 Custo de materiais por unidade produzida
                  </span>
                  <strong style={{ fontSize: 14, color: "#2563eb" }}>
                    {fmtBRL(res.custoIngredientes)}
                  </strong>
                </div>
              </div>
            )}

            <button className={styles.btnAdd} onClick={addIng}>
              <IcoPlus /> Adicionar ingrediente
            </button>
          </Section>

          {/* ── Tempo / Mão de obra ── */}
          <Section title="Tempo de produção" icon="⏱️"
            badge={res.custoMaoObra > 0 ? fmtBRL(res.custoMaoObra) + "/un" : null}
            open={sec.tempo} onToggle={() => togSec("tempo")}>

            <p className={styles.sectionDesc}>
              Informe o tempo total de trabalho e <strong>quantas unidades você produz nesse tempo</strong>.
              O custo será dividido pela quantidade — se faz 20 bolos em 3h, cada um leva apenas a fração do seu tempo.
            </p>

            <div className={styles.grid3}>
              <Field label="Horas trabalhadas">
                <NumInp suffix="h" placeholder="0" value={calc.tempoHoras}
                  onChange={e => setC("tempoHoras", e.target.value)} />
              </Field>
              <Field label="Minutos trabalhados">
                <NumInp suffix="min" placeholder="0" min="0" max="59" value={calc.tempoMinutos}
                  onChange={e => setC("tempoMinutos", e.target.value)} />
              </Field>
              <Field label="Valor por hora">
                <NumInp prefix="R$" placeholder="0,00" value={calc.valorHora}
                  onChange={e => setC("valorHora", e.target.value)} />
              </Field>
            </div>

            <Field label="Unidades produzidas nesse tempo" hint="quantos itens saem nessa sessão de trabalho">
              <NumInp
                placeholder="Ex: 20 bolos"
                min="1" step="1"
                value={calc.tempoUnidadesLote}
                onChange={e => setC("tempoUnidadesLote", e.target.value)}
                style={{ maxWidth: 180 }}
              />
            </Field>

            <CalcBox
              show={res.custoLoteTempo > 0}
              label="Mão de obra"
              lote={res.custoLoteTempo}
              unidade={res.custoMaoObra}
            />
          </Section>

          {/* ── Gás ── */}
          <Section title="Gás" icon="🔥"
            badge={calc.gas.ativo && res.custoGas > 0 ? fmtBRL(res.custoGas) + "/un" : null}
            open={sec.gas} onToggle={() => togSec("gas")}>

            <Toggle checked={calc.gas.ativo} onChange={v => setGas("ativo", v)}
              label="Incluir custo de gás no cálculo" />

            {calc.gas.ativo && (
              <div className={styles.toggleContent}>
                <p className={styles.sectionDesc} style={{ marginBottom: 12 }}>
                  O custo da sessão de gás é calculado pela proporção de tempo usado.
                  Informe <strong>quantas unidades você produz nessa sessão</strong> para dividir corretamente.
                </p>

                <div className={styles.grid2}>
                  <Field label="Valor mensal do botijão">
                    <NumInp prefix="R$" placeholder="0,00" value={calc.gas.valorMensal}
                      onChange={e => setGas("valorMensal", e.target.value)} />
                  </Field>
                  <Field label="Minutos ligado" hint="nesta sessão">
                    <NumInp suffix="min" placeholder="0" value={calc.gas.minutosUso}
                      onChange={e => setGas("minutosUso", e.target.value)} />
                  </Field>
                  <Field label="Total min/mês estimado">
                    <NumInp suffix="min" placeholder="3000" value={calc.gas.totalMinutosMes}
                      onChange={e => setGas("totalMinutosMes", e.target.value)} />
                  </Field>
                  <Field label="Unidades produzidas nessa sessão">
                    <NumInp placeholder="Ex: 20" min="1" step="1" value={calc.gas.unidadesLote}
                      onChange={e => setGas("unidadesLote", e.target.value)} />
                  </Field>
                </div>

                <CalcBox
                  show={res.custoGasLote > 0}
                  lote={res.custoGasLote}
                  unidade={res.custoGas}
                />
              </div>
            )}
          </Section>

          {/* ── Energia ── */}
          <Section title="Energia elétrica" icon="⚡"
            badge={calc.energia.ativo && res.custoEnergia > 0 ? fmtBRL(res.custoEnergia) + "/un" : null}
            open={sec.energia} onToggle={() => togSec("energia")}>

            <Toggle checked={calc.energia.ativo} onChange={v => setEnergia("ativo", v)}
              label="Incluir custo de energia no cálculo" />

            {calc.energia.ativo && (
              <div className={styles.toggleContent}>
                <p className={styles.sectionDesc} style={{ marginBottom: 12 }}>
                  O custo da sessão é proporcional às horas de uso.
                  Informe <strong>quantas unidades produz nessa sessão</strong> para dividir o custo corretamente.
                </p>

                <div className={styles.grid2}>
                  <Field label="kWh consumido por mês">
                    <NumInp suffix="kWh" placeholder="150" value={calc.energia.kwhMes}
                      onChange={e => setEnergia("kwhMes", e.target.value)} />
                  </Field>
                  <Field label="Valor por kWh">
                    <NumInp prefix="R$" placeholder="0.85" value={calc.energia.valorKwh}
                      onChange={e => setEnergia("valorKwh", e.target.value)} />
                  </Field>
                  <Field label="Horas ligado" hint="nesta sessão">
                    <NumInp suffix="h" placeholder="0" value={calc.energia.horasUso}
                      onChange={e => setEnergia("horasUso", e.target.value)} />
                  </Field>
                  <Field label="Total horas/mês estimado">
                    <NumInp suffix="h" placeholder="720" value={calc.energia.totalHorasMes}
                      onChange={e => setEnergia("totalHorasMes", e.target.value)} />
                  </Field>
                  <Field label="Unidades produzidas nessa sessão" style={{ gridColumn: "1 / -1" }}>
                    <NumInp placeholder="Ex: 20" min="1" step="1" value={calc.energia.unidadesLote}
                      onChange={e => setEnergia("unidadesLote", e.target.value)}
                      style={{ maxWidth: 200 }} />
                  </Field>
                </div>

                <CalcBox
                  show={res.custoEnergiaLote > 0}
                  lote={res.custoEnergiaLote}
                  unidade={res.custoEnergia}
                />
              </div>
            )}
          </Section>

          {/* ── Extras + Sistemas ── */}
          <Section title="Custos extras, embalagem e sistemas" icon="📎"
            badge={calc.extras.length > 0 ? `${calc.extras.length}` : null}
            open={sec.extras} onToggle={() => togSec("extras")}>

            <p className={styles.sectionDesc}>
              Embalagens, rótulos, frete, sistemas mensais (Canva, iFood, Adobe…).
              Use <strong>Por unidade</strong> para custo fixo por item, ou <strong>Mensal ÷ prod.</strong> para dividir mensalidade.
            </p>

            {calc.extras.length === 0
              ? <p className={styles.emptyHint}>Nenhum item adicionado ainda.</p>
              : calc.extras.map(e => (
                  <ExtraCard key={e.id} extra={e} onChange={setExtra} onRemove={delExtra} />
                ))
            }

            {res.custoExtras > 0 && (
              <div className={styles.calcInfo} style={{ marginTop: 4 }}>
                <span>Total extras por unidade</span>
                <strong>{fmtBRL(res.custoExtras)}</strong>
              </div>
            )}

            <button className={styles.btnAdd} onClick={addExtra}>
              <IcoPlus /> Adicionar item
            </button>
          </Section>

          {/* ── Margens ── */}
          <Section title="Margem de lucro e impostos" icon="📊"
            open={sec.margens} onToggle={() => togSec("margens")}>

            <div className={styles.margemSection}>
              <Field label={`Margem de lucro — ${margemNum}%`}>
                <div className={styles.rangeRow}>
                  <input type="range" min="5" max="80" step="1"
                    className={styles.range}
                    value={Math.min(parseNum(calc.margemLucro), 80)}
                    onChange={e => handleMargemSlider(e.target.value)} />
                  <div className={styles.fWrap} style={{ width: 90, flexShrink: 0 }}>
                    <input
                      type="number" min="1" max="99" step="1"
                      className={`${styles.fInput} ${styles.hasSuf}`}
                      value={margemExibida}
                      onChange={e => handleMargemManual(e.target.value)}
                      title="Digite qualquer % de margem, inclusive acima de 80%"
                    />
                    <span className={styles.fSuf}>%</span>
                  </div>
                </div>

                <div className={styles.rangeQuick}>
                  {[20, 30, 40, 50, 60, 70, 80].map(v => (
                    <button key={v} type="button"
                      className={`${styles.qBtn} ${margemNum === v ? styles.qBtnOn : ""}`}
                      onClick={() => handleMargemSlider(String(v))}>
                      {v}%
                    </button>
                  ))}
                  <span className={styles.qSep}>ou acima →</span>
                  {[85, 90].map(v => (
                    <button key={v} type="button"
                      className={`${styles.qBtn} ${styles.qBtnHigh} ${margemNum === v ? styles.qBtnOn : ""}`}
                      onClick={() => { setC("margemManual", String(v)); setC("margemLucro", "80"); }}>
                      {v}%
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Impostos / taxas" hint="opcional">
                <NumInp suffix="%" placeholder="0" value={calc.impostos}
                  onChange={e => setC("impostos", e.target.value)}
                  style={{ maxWidth: 160 }} />
              </Field>
            </div>
          </Section>
        </div>

        {/* ── Resultado ── */}
        <div className={styles.colRight}>
          <div className={styles.resCard}>
            <div className={styles.resHead}>
              <span className={styles.resTitle}>Resultado</span>
              {custoOk && <span className={styles.resCusto}>Custo: {fmtBRL(res.custoTotal)}/un</span>}
            </div>

            {!custoOk ? (
              <div className={styles.resEmpty}>
                <span className={styles.resEmptyIcon}>🧮</span>
                <p>Preencha os ingredientes e custos para ver o resultado</p>
              </div>
            ) : (
              <>
                <div className={styles.breakdown}>
                  <p className={styles.breakTitle}>Composição do custo / unidade</p>
                  <div className={styles.breakList}>
                    {res.detIngredientes.map(i => i.custoPorUnidade > 0 && (
                      <div className={styles.breakItem} key={i.id}>
                        <span>📦 {i.nome || "Ingrediente"}</span>
                        <strong className={styles.valOn}>{fmtBRL(i.custoPorUnidade)}</strong>
                      </div>
                    ))}
                    <div className={styles.breakItem}>
                      <div className={styles.breakItemLeft}>
                        <span>⏱️ Mão de obra</span>
                        {res.unidadesTempoLote > 1 && (
                          <em className={styles.breakMeta}>{fmtBRL(res.custoLoteTempo)} ÷ {res.unidadesTempoLote} un</em>
                        )}
                      </div>
                      <strong className={res.custoMaoObra > 0 ? styles.valOn : styles.valOff}>{fmtBRL(res.custoMaoObra)}</strong>
                    </div>
                    {calc.gas.ativo && (
                      <div className={styles.breakItem}>
                        <div className={styles.breakItemLeft}>
                          <span>🔥 Gás</span>
                          {parseNum(calc.gas.unidadesLote) > 1 && (
                            <em className={styles.breakMeta}>{fmtBRL(res.custoGasLote)} ÷ {calc.gas.unidadesLote} un</em>
                          )}
                        </div>
                        <strong className={res.custoGas > 0 ? styles.valOn : styles.valOff}>{fmtBRL(res.custoGas)}</strong>
                      </div>
                    )}
                    {calc.energia.ativo && (
                      <div className={styles.breakItem}>
                        <div className={styles.breakItemLeft}>
                          <span>⚡ Energia</span>
                          {parseNum(calc.energia.unidadesLote) > 1 && (
                            <em className={styles.breakMeta}>{fmtBRL(res.custoEnergiaLote)} ÷ {calc.energia.unidadesLote} un</em>
                          )}
                        </div>
                        <strong className={res.custoEnergia > 0 ? styles.valOn : styles.valOff}>{fmtBRL(res.custoEnergia)}</strong>
                      </div>
                    )}
                    {res.detExtras.map(e => e.custoUnidade > 0 && (
                      <div className={styles.breakItem} key={e.id}>
                        <span>📎 {e.nome || "Extra"}</span>
                        <strong className={styles.valOn}>{fmtBRL(e.custoUnidade)}</strong>
                      </div>
                    ))}
                  </div>
                  <div className={styles.breakTotal}>
                    <span>Custo total / unidade</span>
                    <strong>{fmtBRL(res.custoTotal)}</strong>
                  </div>
                </div>

                <div className={styles.precos}>
                  <p className={styles.precosTitle}>Sugestão de preço de venda</p>

                  {/* Faixa 1 — Empate */}
                  <div className={styles.precoCard}>
                    <div className={styles.precoTag} data-variant="red">⚠ Empate</div>
                    <div className={styles.precoValor} data-variant="red">{fmtBRL(res.precoEmpate)}</div>
                    <div className={styles.precoLucro} data-variant="red">Lucro: {fmtBRL(res.lucroEmpate)}</div>
                    <div className={styles.precoSub}>Cobre apenas os custos — sem lucro real</div>
                  </div>

                  {/* Faixa 2 — Conservador */}
                  <div className={styles.precoCard}>
                    <div className={styles.precoTag} data-variant="amber">Conservador</div>
                    <div className={styles.precoValor} data-variant="amber">{fmtBRL(res.precoConservador)}</div>
                    <div className={styles.precoLucro} data-variant="amber">Lucro: {fmtBRL(res.lucroConservador)}/un</div>
                    <div className={styles.precoSub}>Margem {res.margemConservador}% — entrada segura</div>
                  </div>

                  {/* Faixa 3 — Ideal */}
                  <div className={`${styles.precoCard} ${styles.precoCardDest}`}>
                    <div className={styles.precoTag} data-variant="blue">★ Ideal</div>
                    <div className={styles.precoValor} data-variant="blue">{fmtBRL(res.precoIdeal)}</div>
                    <div className={styles.precoLucro} data-variant="blue">Lucro: {fmtBRL(res.lucroIdeal)}/un</div>
                    <div className={styles.precoSub}>Margem {margemNum}% — preço recomendado</div>
                  </div>

                  {/* Faixa 4 — Premium */}
                  <div className={styles.precoCard}>
                    <div className={styles.precoTag} data-variant="purple">Premium</div>
                    <div className={styles.precoValor} data-variant="purple">{fmtBRL(res.precoPremium)}</div>
                    <div className={styles.precoLucro} data-variant="purple">Lucro: {fmtBRL(res.lucroPremium)}/un</div>
                    <div className={styles.precoSub}>Margem {res.margemPremium}% — mercado premium</div>
                  </div>
                </div>

                {/* ── NOVO: Preço que estou praticando ── */}
                <div style={{
                  marginTop: 20,
                  padding: "14px 16px",
                  background: "var(--surface-2, #f8fafc)",
                  border: "1.5px dashed var(--border, #cbd5e1)",
                  borderRadius: 10,
                }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: "var(--text-2, #475569)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    📌 Preço que estou praticando
                  </p>
                  <p style={{ fontSize: 12, color: "var(--text-2, #64748b)", marginBottom: 10, lineHeight: 1.5 }}>
                    Se você já vende este produto, informe o preço atual para ver se está tendo lucro, prejuízo e quanto.
                  </p>
                  <NumInp
                    prefix="R$"
                    placeholder="0,00"
                    value={calc.precoPraticado}
                    onChange={e => setC("precoPraticado", e.target.value)}
                    style={{ maxWidth: 200 }}
                  />
                  <AnalisePraticado
                    analise={res.analisePraticado}
                    precoIdeal={res.precoIdeal}
                    precoPraticado={parseNum(calc.precoPraticado)}
                  />
                </div>

                {/* ── NOVO: Insights automáticos ── */}
                <Insights insights={res.insights} />

                <div className={styles.resActions}>
                  <button className={styles.btnPrintFull} onClick={() => setPrintOpen(true)}>
                    <IcoPrint /> Imprimir análise completa
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {printOpen && <ImprimirModal calc={calc} res={res} onClose={() => setPrintOpen(false)} />}
    </div>
  );
}